import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Timer, Zap } from "lucide-react";
import { HomeworkShell } from "./HomeworkShell";
import { hwErr, fmtDate } from "./hwApi";
import { tsApi } from "./HomeworkHome";

function SeriesInner() {
  const navigate = useNavigate();
  const [tree, setTree] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [sel, setSel] = useState({ category: "", chapter: "", topic: "", subtopic: "" });
  const [duration, setDuration] = useState(5);
  const [open, setOpen] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    tsApi.get("/tree").then(({ data }) => setTree(data)).catch(() => setError("Could not load the test bank."));
    tsApi.get("/history").then(({ data }) => setHistory(data.attempts)).catch(() => {});
    tsApi.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const cat = tree?.categories.find((c) => c.category === sel.category);
  const chapters = cat?.chapters || [];
  const chapter = chapters.find((c) => c.chapter === sel.chapter);
  const topic = chapter?.topics.find((t) => t.topic === sel.topic);

  const start = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await tsApi.post("/start", { ...sel, duration_min: duration });
      navigate(`/homework/test/${data.attempt_id}`);
    } catch (e) { setError(hwErr(e)); } finally { setBusy(false); }
  };

  if (!tree) return <div className="tp-wrap"><p className="tp-muted">Loading…</p></div>;

  return (
    <div className="tp-wrap" data-testid="ts-home">
      <div className="tp-eyebrow" style={{ color: "#ea580c" }}>PRANJAL SIR TEST SERIES</div>
      <h1 className="tp-h2" style={{ fontSize: 30 }}>Practice. Improve. Excel.</h1>
      <p className="tp-muted" style={{ maxWidth: 560 }}>Five-question timed tests from questions handpicked by Pranjal Sir. Instant scores with solutions — kept separate from your homework marks.</p>

      {stats && stats.tests_taken > 0 && (
        <div className="tp-grid-4" style={{ marginTop: 16 }} data-testid="ts-stats">
          {[[stats.tests_taken, "Tests taken"], [`${stats.avg_percent}%`, "Average score"], [`${stats.accuracy}%`, "Accuracy"],
            [stats.chapter_stats.length, "Chapters practised"]].map(([v, l]) => (
            <div className="tp-card" key={l} style={{ textAlign: "center" }}>
              <b style={{ fontSize: 22, color: "#ea580c" }}>{v}</b>
              <p className="tp-muted" style={{ margin: 0, fontSize: 12 }}>{l}</p>
            </div>
          ))}
        </div>
      )}

      <div className="tp-panel" style={{ marginTop: 18, border: "2px solid #ea580c" }} data-testid="ts-config">
        <h2 className="tp-h2" style={{ fontSize: 18, margin: 0 }}>Configure your test</h2>
        <div style={{ marginTop: 12 }}>
          <p className="tp-muted" style={{ fontSize: 12, margin: "0 0 6px" }}>1. Class</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="ts-classes">
            {tree.categories.filter((c) => !["class_9", "neet"].includes(c.category)).map((c) => (
              <button key={c.category} className="tp-slot" data-testid={`ts-class-${c.category}`}
                      style={{ cursor: "pointer", border: sel.category === c.category ? "1.5px solid #ea580c" : undefined, background: sel.category === c.category ? "#fff7ed" : undefined }}
                      onClick={() => setSel({ category: c.category, chapter: "", topic: "", subtopic: "" })}>
                {({ class_11: "NEET Class 11", class_12: "NEET Class 12" }[c.category] || c.category_label)} ({c.questions})
              </button>
            ))}
            {tree.categories.length === 0 && <p className="tp-muted">The question bank is still being filled — check back soon.</p>}
          </div>
        </div>
        {sel.category && (
          <div style={{ marginTop: 14 }}>
            <p className="tp-muted" style={{ fontSize: 12, margin: "0 0 6px" }}>2. Chapter / topic (optional — leave unselected for a mixed test)</p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="ts-chapters">
              {chapters.map((c) => (
                <button key={c.chapter} className="tp-slot" data-testid={`ts-chapter-${c.chapter.replace(/\W+/g, "-")}`}
                        style={{ cursor: "pointer", border: sel.chapter === c.chapter ? "1.5px solid #ea580c" : undefined, background: sel.chapter === c.chapter ? "#fff7ed" : undefined }}
                        onClick={() => { setSel((s) => ({ ...s, chapter: s.chapter === c.chapter ? "" : c.chapter, topic: "", subtopic: "" })); setOpen(open === c.chapter ? "" : c.chapter); }}>
                  {c.chapter} ({c.topics.reduce((n, t) => n + t.count, 0)}) {c.topics.some((t) => t.subtopics.length > 0 || true) && (open === c.chapter ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                </button>
              ))}
            </div>
            {sel.chapter && open === sel.chapter && chapter && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, padding: 10, background: "#fff7ed", borderRadius: 10, border: "1px dashed #ea580c" }} data-testid="ts-topics">
                {chapter.topics.map((t) => (
                  <button key={t.topic} className="tp-btn small ghost" data-testid={`ts-topic-${t.topic.replace(/\W+/g, "-")}`}
                          style={{ background: sel.topic === t.topic ? "#fed7aa" : "#fff" }}
                          onClick={() => setSel((s) => ({ ...s, topic: s.topic === t.topic ? "" : t.topic, subtopic: "" }))}>
                    {t.topic} ({t.count})
                  </button>
                ))}
              </div>
            )}
            {sel.topic && topic && topic.subtopics.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }} data-testid="ts-subtopics">
                {topic.subtopics.map((s) => (
                  <button key={s.subtopic} className="tp-btn small ghost" data-testid={`ts-subtopic-${s.subtopic.replace(/\W+/g, "-")}`}
                          style={{ background: sel.subtopic === s.subtopic ? "#fdba74" : "#fff" }}
                          onClick={() => setSel((x) => ({ ...x, subtopic: x.subtopic === s.subtopic ? "" : s.subtopic }))}>
                    {s.subtopic} ({s.count})
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {sel.category && (
          <div style={{ marginTop: 14 }}>
            <p className="tp-muted" style={{ fontSize: 12, margin: "0 0 6px" }}>3. Time limit</p>
            <div style={{ display: "flex", gap: 8 }} data-testid="ts-durations">
              {[5, 10].map((d) => (
                <button key={d} className="tp-slot" data-testid={`ts-duration-${d}`}
                        style={{ cursor: "pointer", border: duration === d ? "1.5px solid #ea580c" : undefined, background: duration === d ? "#fff7ed" : undefined }}
                        onClick={() => setDuration(d)}><Timer size={13} /> {d} minutes</button>
              ))}
            </div>
          </div>
        )}
        {error && <p className="tp-error" role="alert" data-testid="ts-error">{error}</p>}
        <button className="tp-btn" style={{ marginTop: 16, background: "#ea580c", padding: "13px 22px" }} disabled={!sel.category || busy}
                onClick={start} data-testid="ts-start">
          <Zap size={16} /> {busy ? "Preparing…" : `START ${duration}-MINUTE TEST`}</button>
      </div>

      {history.length > 0 && (
        <div className="tp-panel" style={{ marginTop: 18 }} data-testid="ts-history">
          <h2 className="tp-h2" style={{ fontSize: 18, margin: 0 }}>My test history</h2>
          {history.map((a) => (
            <a key={a.attempt_id} href={`/homework/test/${a.attempt_id}`} style={{ textDecoration: "none" }} data-testid={`ts-history-${a.attempt_id}`}>
              <div className="admin-row" style={{ borderTop: "1px solid #e4eaf2", padding: "9px 0" }}>
                <span className="status-pill" style={{ background: "#ffedd5", color: "#9a3412" }}>{a.duration_min} MIN</span>
                <span style={{ flex: 1, color: "#1e3a5f", fontSize: 13 }}>{a.category_label} · {a.scope_label} <small className="tp-muted">· {fmtDate(a.submitted_at)} · {Math.round((a.time_taken_sec || 0) / 60)} min taken</small></span>
                <b style={{ color: (a.score?.percent || 0) >= 60 ? "#15803d" : "#b42318" }} data-testid={`ts-history-score-${a.attempt_id}`}>{a.score?.correct}/{a.score?.total}</b>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TestSeries() {
  return <HomeworkShell><SeriesInner /></HomeworkShell>;
}
